import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import queryString from "query-string";
import { Card, Empty, Select, Spin, Typography, notification } from "antd";
import { WalletOutlined } from "@ant-design/icons";
import { getRiderToursToday } from "../../redux/services/apiService";
import {
  REMAINING_COLOR,
  REMAINING_TEXT,
  formatAmount,
  buildSummary,
  DEMO_TOUR,
} from "./helpers";

const { Title, Text } = Typography;

const StackedProgressBar = ({ segments, remaining, total }) => {
  const safeTotal = total > 0 ? total : 1;
  return (
    <div
      style={{
        display: "flex",
        height: 8,
        borderRadius: 999,
        overflow: "hidden",
        background: REMAINING_COLOR,
        margin: "12px 0 16px",
      }}
    >
      {segments.map((segment) => (
        <div
          key={segment.method}
          style={{
            width: `${(segment.total / safeTotal) * 100}%`,
            background: segment.color,
          }}
        />
      ))}
      {remaining > 0 && (
        <div
          style={{
            width: `${(remaining / safeTotal) * 100}%`,
            background: REMAINING_COLOR,
          }}
        />
      )}
    </div>
  );
};

const Dot = ({ color }) => (
  <span
    style={{
      display: "inline-block",
      width: 10,
      height: 10,
      borderRadius: "50%",
      background: color,
      marginRight: 8,
      verticalAlign: "middle",
    }}
  />
);

const Row = ({ leftDotColor, label, value, valueColor, muted }) => (
  <div
    style={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "8px 0",
    }}
  >
    <span style={{ display: "flex", alignItems: "center" }}>
      {leftDotColor && <Dot color={leftDotColor} />}
      <Text style={muted ? { color: "rgba(0,0,0,0.45)" } : undefined}>{label}</Text>
    </span>
    <Text strong style={valueColor ? { color: valueColor } : undefined}>
      {value}
    </Text>
  </div>
);

const PodAmountCard = ({ summary }) => {
  const { breakdown, podTotal, prepaidTotal, remaining } = summary;
  return (
    <Card style={{ borderRadius: 12 }} bodyStyle={{ padding: 20 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <WalletOutlined style={{ fontSize: 22, color: "rgba(0,0,0,0.65)" }} />
          <Text strong style={{ fontSize: 16, color: "rgba(0,0,0,0.85)" }}>
            POD Amount (To Collect)
          </Text>
        </span>
        <Text strong style={{ fontSize: 22, color: "rgba(0,0,0,0.88)" }}>
          {formatAmount(podTotal)}
        </Text>
      </div>

      <StackedProgressBar segments={breakdown} remaining={remaining} total={podTotal} />

      {breakdown.length === 0 && remaining === 0 && podTotal === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No POD orders in this tour"
          style={{ margin: "12px 0" }}
        />
      ) : (
        <>
          {breakdown.map((segment) => (
            <Row
              key={segment.method}
              leftDotColor={segment.color}
              label={segment.label}
              value={formatAmount(segment.total)}
            />
          ))}
          <div style={{ borderTop: "1px dashed #d9d9d9", margin: "8px 0" }} />
          <Row
            leftDotColor={REMAINING_COLOR}
            label="Remaining to collect"
            value={formatAmount(remaining)}
            valueColor={REMAINING_TEXT}
            muted
          />
        </>
      )}

      {prepaidTotal > 0 && (
        <div
          style={{
            marginTop: 12,
            background: "#fafafa",
            borderRadius: 8,
            padding: "10px 14px",
          }}
        >
          <Row
            label="Pre-paid Orders (Paid)"
            value={formatAmount(prepaidTotal)}
            muted
          />
        </div>
      )}
    </Card>
  );
};

const TourPaymentSummary = () => {
  const location = useLocation();
  const { clientId, authToken, riderId, date, mock } = queryString.parse(location.search);
  const isMock = mock === "1" || mock === "true";

  const [tours, setTours] = useState([]);
  const [loadingTours, setLoadingTours] = useState(false);
  const [selectedTourId, setSelectedTourId] = useState(null);

  useEffect(() => {
    let cancelled = false;

    if (isMock) {
      setTours([DEMO_TOUR]);
      setSelectedTourId(DEMO_TOUR.id);
      return () => {
        cancelled = true;
      };
    }

    if (!clientId || !authToken || !riderId) {
      notification.error({
        message: "Missing parameters",
        description: "clientId, authToken and riderId are all required in the URL.",
      });
      return () => {
        cancelled = true;
      };
    }

    setLoadingTours(true);
    getRiderToursToday({ clientId, authToken }, riderId, date)
      .then((response) => {
        if (cancelled) return;
        if (response?.status >= 200 && response?.status < 300) {
          const nextTours = response?.data?.tours || [];
          setTours(nextTours);
          setSelectedTourId((current) =>
            nextTours.some((tour) => tour?.id === current) ? current : nextTours[0]?.id || null
          );
        } else {
          notification.error({
            message: "Failed to load tours",
            description:
              response?.data?.message || `Request failed with status ${response?.status}`,
          });
          setTours([]);
        }
      })
      .catch((error) => {
        if (cancelled) return;
        notification.error({
          message: "Failed to load tours",
          description: error?.message || "A network error occurred.",
        });
        setTours([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingTours(false);
      });

    return () => {
      cancelled = true;
    };
  }, [clientId, authToken, riderId, date, isMock]);

  const selectedTour = useMemo(
    () => tours.find((tour) => tour?.id === selectedTourId) || null,
    [tours, selectedTourId]
  );

  const summary = useMemo(() => (selectedTour ? buildSummary(selectedTour) : null), [selectedTour]);

  const tourOptions = useMemo(
    () =>
      tours.map((tour) => ({
        value: tour?.id,
        label: `${tour?.providedTourName || tour?.name || tour?.id || "(unnamed tour)"} — ${
          tour?.tourStatus || "—"
        }`,
      })),
    [tours]
  );

  return (
    <div style={{ padding: 16, maxWidth: 480, margin: "0 auto" }}>
      <Title level={5} style={{ marginBottom: 12 }}>
        Tour Payment Summary
      </Title>

      <Spin spinning={loadingTours}>
        <Select
          showSearch
          allowClear
          placeholder="Select today's tour"
          style={{ width: "100%", marginBottom: 16 }}
          options={tourOptions}
          value={selectedTourId || undefined}
          onChange={(value) => setSelectedTourId(value || null)}
          filterOption={(input, option) =>
            (option?.label || "").toString().toLowerCase().includes(input.toLowerCase())
          }
          notFoundContent={
            loadingTours ? <Spin size="small" /> : <Empty description="No tours found for today" />
          }
        />
      </Spin>

      {summary && <PodAmountCard summary={summary} />}
    </div>
  );
};

export default TourPaymentSummary;
